import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Input, InputNumber, Modal, Select, Space, Typography, } from 'antd';
import { useTrackingCode } from "react-hubspot-tracking-code-hook";

import styles from './form-styles.module.scss';
import { StepFormProps } from '../types/types';
import { useShared } from "../context/shared.context";
import TextArea from "antd/es/input/TextArea";

const { Title, Text, Link } = Typography;

export const CompanyInformationForm: React.FC<StepFormProps> = ( props ) => {
  const { onNext, onBack, defaultValues, experianResult, openCompany } = props;
  const [form] = Form.useForm();
  const [formModal] = Form.useForm();

  const { setTrackPageView } = useTrackingCode();
  setTrackPageView();

  const { listCurrencies } = useShared();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalValues, setModalValues] = useState<any>();
  const [companyName, setCompanyName] = useState<string>();
  const [registrationNumber, setCompanyRegistrationNumber] = useState<string>();
  const [companyNotFound, setCompanyNotFound] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [currency, setCurrency] = useState<string>('EUR');
  const [alertInModal, setAlertInModal] = useState<boolean>(false);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    formModal
      .validateFields()
      .then(() => {
        const data = formModal.getFieldsValue();
        setCompanyName(data.companyName);
        setCompanyRegistrationNumber(data.registrationNumber);
        setCompanyNotFound(true);
        setIsModalOpen(false);
        setShowError(false);
      })
      .catch(( info ) => {
        //console.log('Validate Failed:', info);
      });
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  function getMandatoryLabel( label: string ) {
    return <div><Text type="danger" strong>*</Text>{' '}<Text strong>{label}</Text></div>;
  }

  function getOptionalLabel( label: string ) {
    return <div><Text strong>{label}</Text>{' '}<Text type="secondary">(optional)</Text></div>;
  }

  useEffect(() => {
    if (experianResult === undefined || experianResult === null || experianResult?.length === 0) {   // Open the modal when experian didn't get any result
      setAlertInModal(true);
      showModal();
    } else {
      setAlertInModal(false);
    }
  }, [experianResult]);

  useEffect(() => {
    setCompanyName(defaultValues?.companyName);
    setCompanyRegistrationNumber(defaultValues?.registrationNumber);
    form.setFieldsValue(defaultValues);
    window?.scrollTo?.(0, 0);
    window?.dataLayer?.push({
      event: 'pageview',
      page: {
        url: `${window.location.origin}${'/company-information'}${
          window.location.search
        }`,
        title: 'Company Information',
      },
    });

    const registrationForm = JSON.parse(localStorage.getItem('pre-registration-form') || '');
    setModalValues(registrationForm);
    if (registrationForm?.summary?.countryOfIncorporation === 'Other') {
      showModal();
    }
  }, [defaultValues, form]);

  const currencySelector = (
    <Form.Item
      name={['currency']}
      initialValue={'EUR'}
      noStyle
    >
      <Select
        placeholder="Currency"
        onSelect={( value ) => {
          setCurrency(value);
        }}
      >
        {(listCurrencies?.result || []).map(( currency: any ) => (
          <Select.Option
            key={currency.code}
            value={currency.code}
          >
            {currency.name}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  );

  function saveCompanyValues( value: string ) {
    const selectedValuesKeys = experianResult?.filter(( result: any ) => result.number === value);
    if (selectedValuesKeys !== undefined && selectedValuesKeys.length > 0) {
      setCompanyName(selectedValuesKeys[0]?.name);
    }
    setCompanyRegistrationNumber(value);
    setCompanyNotFound(false);
    setShowError(false);
  }

  return (
    <Space direction={'vertical'} size={'large'} className={'full-width'}>
      <Space direction={'vertical'} size={'small'}>
        <Title
          style={{ margin: 0 }}
          level={2}
          data-cy="app-page-title"
        >
          About Your Company
        </Title>
        <Text type={'secondary'}>
          Now, we need some top level information about your Company.
        </Text>
      </Space>

      <Form
        requiredMark={false}
        name="company-information-form"
        onFinish={async ( data ) => {
          data.name = companyName;
          data.registrationNumber = registrationNumber;
          data.currency = currency;
          data.country = (modalValues?.summary?.countryOfIncorporation !== 'Other') ? modalValues.summary.countryOfIncorporation : null;
          if (companyName) {
            onNext?.(data);
          }
        }}
        autoComplete="off"
        form={form}
        layout={'vertical'}
        className={styles['sections-wrapper']}
      >
        <Space direction={'vertical'} size={'large'} className={'full-width'}>
          <Space direction={'vertical'} size={'small'} className={'full-width'}>
            <Form.Item
              label={getMandatoryLabel("Registered company")}
              rules={[{ required: true, message: 'This field is required' }]}
              extra={showError ? <Text type={'danger'}>This field is required</Text> : null}
              help={companyNotFound ? <Link onClick={showModal}> Do you want to modify your company? </Link> :
                <Link onClick={showModal}> Did not find your company? </Link>}
              style={{ marginBottom: '24px'}}
              className={showError ? styles['error'] : ''}
            >
              <Select
                size={'large'}
                autoFocus={openCompany}
                defaultOpen={openCompany}
                placeholder={'Select a company'}
                data-cy="company-information-company-name-search-input"
                onChange={( value ) => {
                  saveCompanyValues(value);
                }}
                value={companyName ? `${companyName} (${registrationNumber})` : undefined}
              >
                {experianResult?.map(( company: any ) => (
                  <Select.Option key={company.number} value={company.number}>
                    {<div>{company.name} ({company.number})</div>}
                  </Select.Option>
                ))}
                {companyNotFound && <Select.Option key={companyName} value={registrationNumber}>
                  {<div>{companyName} ({registrationNumber})</div>}
                </Select.Option>}
              </Select>
            </Form.Item>

            <Form.Item
              label={getMandatoryLabel("How much funding do you require?")}
              name={['fundingRequired']}
              rules={[{ required: true, message: 'This field is required' }]}
              extra={<Text type="secondary">We only approve funding greater than <span>&#8364;</span>50,000</Text>}
            >
              <InputNumber
                size="large"
                style={{ width: '100%' }}
                data-cy="company-information-funding-requirement-input"
                formatter={( value ) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                addonBefore={currencySelector}
                placeholder="Funding amount"
              />
            </Form.Item>


            <Form.Item
              label={getOptionalLabel('What business model do you operate?')}
              name={['businessModel']}
            >
              <Select
                size="large"
                placeholder="Select an option"
                data-cy="company-information-business-model-select"
              >
                <Select.Option key={'b2b'} value={'b2b'}>
                  B2B
                </Select.Option>
                <Select.Option key={'b2c'} value={'b2c'}>
                  B2C
                </Select.Option>
                <Select.Option key={'b2bAndB2c'} value={'b2bAndB2c'}>
                  Both
                </Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              label={getMandatoryLabel("Annual Turnover for last 12 months")}
              name={['turnover']}
              rules={[{ required: true, message: 'This field is required' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                size="large"
                placeholder="Turnover for last 12 months"
                data-cy="company-information-turnover-input"
                formatter={( value ) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
              />
            </Form.Item>

            <Form.Item
              label={getOptionalLabel('Annual Turnover for next 12 months')}
              name={['forecastTurnover']}
            >
              <InputNumber
                style={{ width: '100%' }}
                size="large"
                placeholder="Turnover for next 12 months"
                data-cy="company-information-turnover-next-input"
                formatter={( value ) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
              />
            </Form.Item>

            {/* <Form.Item
              label={<Text strong>Which solution are you most interested in?</Text>}
              name="selectedProducts"
            >
              <Select
                placeholder="Select an option"
                size="large"
                mode="multiple"
                data-cy="company-information-products-select"
                allowClear>
                <Option value="line-of-credit">
                  Line of Credit
                </Option>
                <Option value="invoice-discounting">
                  Invoice Discounting
                </Option>
                <Option value="revenue-based-finance">
                  Revenue Based Finance
                </Option>
                <Option value="development-finance">
                  Development Finance
                </Option>
              </Select>
            </Form.Item>*/}

            {/* <Form.Item
              name={['accountancy_package']}
              rules={[{ required: true, message: 'This field is required' }]}
              label={'What accountancy package does your company currently use?*'}
            >
              <Select
                size="large"
                placeholder="Select an option"
                data-cy="company-information-accountancy-package-select"
              >
                {(listAccountancyPackage?.result || []).map(( accountancyPackage: any ) => (
                  <Select.Option
                    key={accountancyPackage.key}
                    value={accountancyPackage.key}
                  >
                    {accountancyPackage.text}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

*/}
            <Form.Item
              label={getOptionalLabel('Additional Information')}>
              <Form.Item
                name="comments"
              >
                <TextArea
                  placeholder="Is there any other information you would like to share with us?"
                  data-cy="company-information-additional-info-input"
                  rows={4}
                  showCount
                  maxLength={200}
                />
              </Form.Item>
            </Form.Item>

          </Space>
          <Space direction={'vertical'} size={'small'} className={'full-width'}>
           <Button
              block
              type="primary"
              htmlType="submit"
              data-cy="app-page-submit-button"
              onClick={() => {
                (companyName === undefined) ? setShowError(true) : setShowError(false)
              }}
            >
              Submit
            </Button>

            <Button
              block
              type="default"
              data-cy="app-page-back-button"
              htmlType="button"
              onClick={() => {
                const fieldsValue = form.getFieldsValue();
                fieldsValue.companyName = companyName;
                fieldsValue.registrationNumber = registrationNumber;

                onBack?.(fieldsValue);
              }}
            >
              Back
            </Button>
          </Space>
        </Space>
      </Form>
      <Modal title="Company Information" open={isModalOpen} onOk={handleOk} onCancel={handleCancel} okText="Confirm">
        <Form
          requiredMark={false}
          name="contact-information-form-step-2"
          autoComplete="off"
          form={formModal}
          layout={'vertical'}
          className={styles['sections-wrapper']}
        >
          <Space direction={'vertical'} size={'large'} className={'full-width'}>
            {
              alertInModal &&
                <Alert
                    message="We couldn’t find a record of your company name."
                    description='Please fill in your company name and registration number.'
                    type="warning"
                    showIcon
                />
            }

            <Space direction={'vertical'} size={'small'} className={'full-width'}>
              <Form.Item
                label={getMandatoryLabel("Registered company")}
                name={['companyName']}
                initialValue={(modalValues?.company?.companyName) ? modalValues?.company?.companyName : modalValues?.summary?.companyName}  
                rules={[{ required: true, message: 'This field is required' }]}
              >
                <Input
                  size="large"
                  placeholder={'The exact legally registered name of the company'}
                  data-cy="company-information-company-name-search-input"></Input>
              </Form.Item>

              <Form.Item
                label={getMandatoryLabel("Company registration number")}
                name={['registrationNumber']}
                initialValue={modalValues?.company?.registrationNumber}
                rules={[{ required: true, message: 'This field is required' }]}
              >
                <Input
                  size="large"
                  placeholder={'Registration Number'}
                  data-cy="company-information-company-name-search-input"></Input>
              </Form.Item>
            </Space>
          </Space>
        </Form>
      </Modal>
    </Space>
  );
};
